// Currency display helpers. Two explicit units so a call site can never be
// off by 100x: backend billing amounts are dollar strings/numbers, catalog
// prices are integer cents.
export function formatMoneyDollars(value: string | number): string {
    return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function formatMoneyCents(cents: number): string {
    return formatMoneyDollars(cents / 100);
}

// Narrows a nullable/possibly-missing cents value to a genuine finite number,
// so callers can filter untracked amounts out of Math.min/Math.max instead of
// letting a stray `null`/`undefined` produce NaN.
export function isFiniteCents(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

export function formatMoneyCentsOrDash(cents: number | null | undefined): string {
    return isFiniteCents(cents) ? formatMoneyCents(cents) : '-';
}

// ---- charge preview math (integer cents) ----
// Mirrors backend/src/routes/api/v2/billing/charges/charges.process.service.ts
// exactly, so the charge-creation preview matches what processing actually
// invoices to the cent.

// Largest-remainder allocation: shares are floor-scaled, then leftover cents
// go to the largest weights first. Sums to totalCents exactly.
export function allocateProportionallyCents(weights: number[], totalCents: number): number[] {
    const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
    if (weightSum <= 0) {
        return weights.map((_, index) => (index === 0 ? totalCents : 0));
    }
    const shares = weights.map((weight) => Math.floor((weight * totalCents) / weightSum));
    let remainder = totalCents - shares.reduce((sum, share) => sum + share, 0);
    const byWeightDesc = weights
        .map((weight, index) => ({ weight, index }))
        .sort((a, b) => b.weight - a.weight);
    for (const { index } of byWeightDesc) {
        if (remainder <= 0) break;
        shares[index] += 1;
        remainder -= 1;
    }
    return shares;
}

export function installmentShareCents(
    lineCents: number,
    installments: number,
    installmentIndex: number
): number {
    const base = Math.floor(lineCents / installments);
    if (installmentIndex === installments - 1) {
        return lineCents - base * (installments - 1);
    }
    return base;
}
